// Balancemåling: spiller banerne headless med tre botmodeller og to nybegyndere og giver
// vinderprocent pr. bane og sværhed. Bruges af balance-porten i test/balance.test.ts.
//
// Fuld tabel: BALANCE_SEEDS=20 npm run balance

import { autoplayBrain, type AutoplayModel } from './autoplay';
import { adviceIntent, advise } from './coach';
import { legalActions } from '../sim/actions';
import { nodeById, dist, degree } from '../sim/grid';
import { createState, step } from '../sim';
import type { DifficultyId, Intent, PlayerId, State } from '../sim';

export type Player = AutoplayModel | 'newbie' | 'coached';

/**
 * En fornuftig nybegynder: bygger én luftledning pr. mørk by til nærmeste producent, sender
 * montøren til brud og vælger første pakke. Opgraderer aldrig og bygger ingen dobbeltveje.
 * `coached` følger først spillets eget råd (det hintet og hånden viser) og ellers det samme.
 */
export function newbieBrain(coached = false, player: PlayerId = 'p1', lazy = false): (state: State) => Intent | null {
  return (state) => {
    if (state.tick % 40 !== 0) return null;
    if (coached) {
      // `lazy`: tager kun de akutte råd og lader batteri, vej nr. 2 og den ubrugte mølle ligge
      // (tillidstestens spiller, der ikke gør, hvad hintet siger).
      const all = advise(state);
      const a = lazy && all && !all.urgent ? null : all;
      const it = a ? adviceIntent(state, a, player) : null;
      if (it) return it;
    }
    const legal = legalActions(state, player);
    const pack = legal.find((c) => c.kind === 'pick_pack');
    if (pack) return { player, clientSeq: 0, kind: 'pick_pack', opt: pack.opt, source: 'bot' };
    const fix = legal.find((c) => c.kind === 'dispatch_crew');
    if (fix) return { player, clientSeq: 0, kind: 'dispatch_crew', target: fix.target, source: 'bot' };
    let best: { target: string; d: number } | null = null;
    for (const c of legal) {
      if (c.kind !== 'build_line' || c.opt !== 'air' || !c.target) continue;
      const [a, b] = c.target.split('|').map((id) => nodeById(state, id));
      if (!a || !b) continue;
      const city = a.city ? a : b.city ? b : null;
      const other = city === a ? b : a;
      if (!city || !other || degree(state, city.id) > 0) continue;
      if (!other.producer && degree(state, other.id) === 0) continue;
      const d = dist(a, b);
      if (!best || d < best.d) best = { target: c.target, d };
    }
    if (best) return { player, clientSeq: 0, kind: 'build_line', target: best.target, opt: 'air', source: 'bot' };
    // Ny producent uden forbindelse: kobl den til nærmeste net.
    for (const c of legal) {
      if (c.kind !== 'build_line' || c.opt !== 'air' || !c.target) continue;
      const [a, b] = c.target.split('|').map((id) => nodeById(state, id));
      if (!a || !b) continue;
      const lone = a.producer && degree(state, a.id) === 0 ? a : b.producer && degree(state, b.id) === 0 ? b : null;
      const other = lone === a ? b : a;
      if (lone && other && degree(state, other.id) > 0 && dist(a, b) < 40) {
        return { player, clientSeq: 0, kind: 'build_line', target: c.target, opt: 'air', source: 'bot' };
      }
    }
    return null;
  };
}

export interface Outcome {
  won: boolean;
  stars: number;
  year: number;
  lines: number;
}

export function playOnce(level: string, diff: DifficultyId, seed: number, player: Player): Outcome {
  const state = createState(level, diff, seed);
  const brain = player === 'newbie' || player === 'coached' ? newbieBrain(player === 'coached') : autoplayBrain(player, seed);
  let seq = 0;
  for (let i = 0; i < 30_000 && state.phase !== 'won' && state.phase !== 'lost'; i++) {
    const it = brain(state);
    const intents: Intent[] = [];
    if (it) intents.push({ ...it, clientSeq: ++seq });
    step(state, intents);
  }
  return { won: state.phase === 'won', stars: state.result?.stars ?? 0, year: state.year, lines: state.lines.length };
}

export function winRate(level: string, diff: DifficultyId, player: Player, seeds: number): { rate: number; outcomes: Outcome[] } {
  const outcomes: Outcome[] = [];
  for (let s = 1; s <= seeds; s++) outcomes.push(playOnce(level, diff, s, player));
  return { rate: outcomes.filter((o) => o.won).length / seeds, outcomes };
}

export const BALANCE_ROWS: [string, DifficultyId][] = [
  ['01-foerste-vind', 'laerling'],
  ['01-foerste-vind', 'vagthavende'],
  ['02-thor-gaar-i-land', 'laerling'],
  ['02-thor-gaar-i-land', 'vagthavende'],
];
export const BALANCE_PLAYERS: Player[] = ['newbie', 'coached', 'novice', 'average', 'expert'];

/** Hele tabellen som tekst, én linje pr. bane og sværhed. */
export function balanceTable(seeds: number): string[] {
  return BALANCE_ROWS.map(([level, diff]) => {
    const cells = BALANCE_PLAYERS.map((p) => {
      const r = winRate(level, diff, p, seeds);
      const lines = Math.round(r.outcomes.reduce((a, o) => a + o.lines, 0) / seeds);
      return `${p} ${Math.round(r.rate * 100)}% (${lines} l)`;
    });
    return `${level.slice(0, 2)} ${diff.padEnd(12)} ${cells.join('  ')}`;
  });
}
